import {
  Edge,
  createEdgeObject,
  getEdgeDesignInitialComputedValue,
  getEdgeDesignFromTemplate,
  getEdgeDesignSyncedToOriginal,
  convertOriginalToEdgeProperty,
  convertToOriginalEdgeProperty,
  adaptedOriginalDesignPropertiesToEdge
} from '@/modules/Edge';
import ToastUtils from '@/utils/ToastUtils';
import {TOASTS_TYPES} from '@/models/TOASTS_TYPES';
import Fonts from '@/services/canvas/fonts/Fonts';
import { getMapAreaService } from '@/modules/map-editor/composables/useMapArea';
import { EDITING_ELEM_TYPES } from "@/models/EDITING_ELEM_TYPES";

const {fontFamilyGetNameByUrl, fontFamilyGetUrlByName} = Fonts();
const {createToastFromStore} = ToastUtils();
import ApiService from '@/utils/ApiService';
import {SE_SYNC_TYPES} from '@/models/SE_SYNC_TYPES';
import useI18nGlobal from '@/utils/i18n';

const {i18n} = useI18nGlobal();
const {t} = i18n.global;

// MODULES
import create from '@/store/modules/map/edges/modules/create';
import remove from '@/store/modules/map/edges/modules/remove';
import visibility from "@/store/modules/map/edges/modules/visibility";
import design from "@/store/modules/map/edges/modules/design";
import content from "@/store/modules/map/edges/modules/content";

export default {
  namespaced: true,
  modules: {
    create,
    remove,
    design,
    visibility,
    content,
  },
  state: () => ({
    rawEdges: null,
    edges: null,
    edgesURL: '/edges/',
    selectedEdgeId: null,
    edgesHidden: false,
    edgesLocked: false,
    contentOrder: {},
    visibilityOrder: [],
    contentTimeout: null,
    visibilityTimeout: null,
    edgeConnections: {
      from: null,
      to: null
    }
  }),
  getters: {
    getEdges(state) {
      return state.edges;
    },
    getRawEdges(state) {
      return state.rawEdges;
    },
    getEdgesHidden(state) {
      return state.edgesHidden
    },
    getEdgesLocked(state) {
      return state.edgesLocked
    },
    getSelectedEdgeId(state) {
      return state.selectedEdgeId
    },
    getSelectedEdge(state) {
      return state.edges.find(edge => edge.id === state.selectedEdgeId);
    },
    getSelectedRawEdge(state) {
      return state.rawEdges.find(edge => edge.id === state.selectedEdgeId);
    },
    getNodes(state, getters, rootState, rootGetters) {
      return rootGetters['nodes/getNodes'];
    },
    getEdgesURL(state) {
      return state.edgesURL;
    },
    getMapsURL(state, getters, rootState, rootGetters) {
      return rootGetters["maps/getMapsURL"]
    },
    getCurrentMap(state, getters, rootState, rootGetters) {
      return rootGetters["maps/getCurrentMap"]
    },
    getElementsURL(state, getters, rootState, rootGetters) {
      return rootGetters["maps/getElementsURL"]
    },
    getElementsDesign(state, getters, rootState, rootGetters) {
      return rootGetters["design/getElementsDesign"]
    },
    getClientFonts(state, getters, rootState, rootGetters) {
      return rootGetters["fonts/getClientFonts"]
    },
    getLayersAmount(state, getters, rootState) {
      return rootState.mapSettings.mapData.zoomLevels
    },
    getCurrentLayerId(state, getters, rootState) {
      return rootState.layers.currentLayerId
    },
    getEdgeConnections(state) {
      return state.edgeConnections;
    },
    isTemplateChangesTracked(state, getters, rootState) {
      const activeEdge = state.rawEdges.find(edge => edge.id === state.selectedEdgeId);
      const templateId = activeEdge.design.object_template;

      // Same semantics as nodes/isTemplateChangesTracked: null/undefined in
      // edge.design means "no override" (inherit from template/defaults); a
      // non-null value diverging from the template baseline is an override.
      for (let [key, value] of Object.entries(activeEdge.design)) {
        if (key === 'object_template' || value == null) continue;
        if (value !== getEdgeDesignInitialComputedValue(templateId, rootState.design.design, key)) {
          return true
        }
      }
      return false
    },
    getChosenTemplate(state, getters, rootState, rootGetters){
      return rootGetters["templates/getObjectTemplateChosen"]
    }
  },
  mutations: {
    SAVE_RAW_EDGES(state, edges) {
      state.rawEdges = edges;
    },
    POPULATE_EDGES(state, edgesData) {
      const {nodes, elementsDesign, fonts} = edgesData;
      state.edges = [];
      const unlinkedEdges = JSON.parse(JSON.stringify(state.rawEdges));
      
      unlinkedEdges.forEach(edge => {
        state.edges.push(new Edge(createEdgeObject(edge, nodes, elementsDesign, fonts)))
      })
    },
    TOGGLE_EDGES_HIDDEN(state) {
      state.edgesHidden = !state.edgesHidden;
      state.edges.forEach(edge => {
        edge.hidden = !edge.hidden;
      })
    },
    TOGGLE_EDGES_LOCKED(state) {
      state.edgesLocked = !state.edgesLocked;
      state.edges.forEach(edge => {
        edge.locked = !edge.locked;
      })
    },
    UPDATE_EDGE(state, data) {
      const updateData = {...data};
      const {edgeId} = updateData;
      const edge = state.edges.find(edge => edge.id === parseInt(edgeId));
      if (!edge) {
        console.log('No edge found to update: ' + updateData);
        return;
      }
      
      getMapAreaService().updateElement(true, edgeId, EDITING_ELEM_TYPES.EDGE);
      
      delete updateData.edgeId;
      Object.assign(edge, updateData);
    },
    UPDATE_EDITED_EDGE(state, {edgeData, edgeId}) {
      const edge = state.edges.find(edge => edge.id === edgeId);
      if (edge) {
        Object.assign(edge, edgeData);
      }
    },
    SET_SELECTED_EDGE(state, edgeID) {
      state.selectedEdgeId = edgeID;
    },
    CHANGE_ZOOM_LEVEL(state, zoomData) {
      const {zoomLevel, previous} = zoomData;
      state.edges.forEach(edge => {
        if (edge.visibilityMax >= zoomLevel) {
          edge.visibilityMax = zoomLevel;
        } else if (edge.visibilityMax >= previous && zoomLevel > previous) {
          edge.visibilityMax = zoomLevel;
        }
        
        if (edge.visibilityMin > zoomLevel) {
          edge.visibilityMin = zoomLevel;
        }
      })
    },
    APPLY_TEMPLATE_RESTORE_TO_EDGES(state, data) {
      const {elementsDesign, fonts, edgeId} = data;
      const currentEdgeOriginal = state.rawEdges.find(edge => edge.id === edgeId);
      let currentEdge = state.edges.find(edge => edge.id === edgeId);
      const templateId = currentEdge.template;
      
      if (!currentEdgeOriginal || !currentEdge || !templateId) return;
      
      let restoredProperties = {}
      for (let [key, value] of Object.entries(currentEdgeOriginal.design)) {
        if (key !== 'object_template' && value !== null) {
          currentEdgeOriginal.design[key] = null;
          Object.assign(restoredProperties, {[key]: value})
        }
      }
      
      let convertedData = {};
      Object.entries(restoredProperties).forEach(([property, value]) => {
        Object.assign(
          convertedData,
          convertOriginalToEdgeProperty({[property]: getEdgeDesignInitialComputedValue(templateId, elementsDesign, property)}))
      });
      
      convertedData.fontFamily = fontFamilyGetNameByUrl(convertedData.fontFamily, fonts);
      Object.assign(currentEdge, convertedData)
    },
    NULLIFY_EDGE_ORIGINAL_VALUES(state, edgeId) {
      const currentEdgeId = edgeId !== null && edgeId !== undefined ? edgeId : state.selectedEdgeId;
      const currentEdgeOriginal = state.rawEdges.find(edge => edge.id === currentEdgeId);
      
      if (!currentEdgeOriginal) return;
      
      for (let [key, value] of Object.entries(currentEdgeOriginal.design)) {
        if (key !== 'object_template' && value !== null) {
          currentEdgeOriginal.design[key] = null;
        }
      }
    },
    UPDATE_EDGE_TEMPLATE_ID(state, {edgeId, templateId}) {
      const edgeIndex = state.edges.findIndex(edge => edge.id === edgeId);
      if (edgeIndex !== undefined && edgeIndex !== -1 && state.edges) {
        state.edges[edgeIndex].template = templateId !== null ? templateId.toString() : templateId;
      }
    },
    UPDATE_EDGE_ORIGINAL_TEMPLATE_ID(state, {templateId, edgeId}) {
      const edgeIndex = state.rawEdges.findIndex(edge => edge.id === edgeId);
      if (edgeIndex !== undefined && edgeIndex !== -1 && state.rawEdges) {
        state.rawEdges[edgeIndex].design.object_template = templateId !== null ? templateId.toString() : templateId;
      }
    },
    APPLY_OBJECT_TEMPLATES_UPDATED_TO_EDGES(state, {templateId, elementsDesign, fonts}) {
      const edgesToUpdate = state.edges.filter(edge => edge.template === templateId.toString()) || [];
      
      edgesToUpdate.forEach(edge => {
        Object.assign(edge, getEdgeDesignSyncedToOriginal(edge.id, state.rawEdges, elementsDesign, fonts));
        getMapAreaService().updateElement(true, edge.id, EDITING_ELEM_TYPES.EDGE);
      })
    },
    APPLY_OBJECT_TEMPLATES_DELETED(state, templateData) {
      const {templateId, elementsDesign, fonts} = templateData;
      const edgesOriginalToUpdate = state.rawEdges.filter(edge => edge.design.object_template === templateId.toString()) || [];
      
      edgesOriginalToUpdate.forEach(edge => {
        edge.design.object_template = "0";
      })
      
      const edgesToUpdate = state.edges.filter(edge => edge.template === templateId.toString()) || [];
      edgesToUpdate.forEach(edge => {
        edge.template = "0";
        Object.assign(edge, getEdgeDesignSyncedToOriginal(edge.id, state.rawEdges, elementsDesign, fonts));
      })
    },
    APPLY_GLOBAL_UPDATE_TO_EDGES(state, edgeData) {
      const {elementsDesign, fonts} = edgeData;
      state.edges.forEach(edge => {
        Object.assign(edge, getEdgeDesignSyncedToOriginal(edge.id, state.rawEdges, elementsDesign, fonts));
      })
    },
    CONNECT_EDGE(state, nodeId) {
      state.edgeConnections.from === null ?
        state.edgeConnections.from = nodeId :
        state.edgeConnections.to = nodeId;
    },
    CONNECT_CLEAR(state) {
      state.edgeConnections.from = null;
      state.edgeConnections.to = null;
    },
    SET_EDGE_EDITING(state, edgeEditing) {
      const edgeIndex = state.edges.findIndex(edge => edge.id === edgeEditing.elem_id);
      if (edgeIndex !== undefined && edgeIndex !== -1 && state.edges) {
        state.edges[edgeIndex].editing = edgeEditing;
      }
    },
    REMOVE_EDGE_EDITING(state, edgeEditing) {
      const edgeIndex = state.edges.findIndex(edge => edge.id === edgeEditing.elem_id);
      if (edgeIndex !== undefined && edgeIndex !== -1 && state.edges) {
        state.edges[edgeIndex].editing = null;
      }
    },
    UPDATE_EDGE_HIDDEN_BY_NODE(state, {nodes, hiddenData}){
      const edges = state.edges || [];
      
      for(let [id, status] of Object.entries(hiddenData)){
        const relatedEdges = edges.filter(edge =>
          edge.fromNodeId === parseInt(id) || edge.toNodeId === parseInt(id)
        );
        
        const node = nodes.find(node => node.id === parseInt(id));
        if(!status && node.hidden) return;
        
        relatedEdges.forEach(edge => edge.hidden = status);
      }
    }
  },
  actions: {
    saveRawEdges({commit}, edges) {
      commit('SAVE_RAW_EDGES', edges);
    },
    populateEdges({getters, commit}) {
      const nodes = getters.getNodes;
      const elementsDesign = getters.getElementsDesign;
      const fonts = getters.getClientFonts;
      commit('POPULATE_EDGES', {nodes, elementsDesign, fonts});
    },
    toggleEdgesHidden({commit}) {
      commit('TOGGLE_EDGES_HIDDEN');
      getMapAreaService().renderFrame();
    },
    toggleEdgesLocked({commit}) {
      commit('TOGGLE_EDGES_LOCKED');
      getMapAreaService().renderFrame();
    },
    updateEdge({getters, commit}, updateData) {
      commit('UPDATE_EDGE', updateData);
    },
    setSelectedEdge({state, commit, dispatch}, edgeID) {
      commit('SET_SELECTED_EDGE', edgeID);
    },
    saveEditedEdge({getters, commit}, {edgeId, edgeData}) {
      commit('UPDATE_EDITED_EDGE', {edgeId, edgeData});
    },
    changeEdgesZoomLevel({commit}, zoomData) {
      commit('CHANGE_ZOOM_LEVEL', zoomData);
    },
    connectEdge({getters, commit, dispatch}, nodeId) {
      commit('CONNECT_EDGE', nodeId);
    },
    cancelEdgeConnection({getters, commit, dispatch}) {
      const nodeId = getters.getEdgeConnections.from;
      if (nodeId !== null) dispatch('nodes/unmarkNodeConnect', nodeId, {root: true})
      dispatch('nodes/markNodesUnavailableForConnect', null, {root: true});
      commit('CONNECT_CLEAR');
    },
    updateEdgeOnFontLoaded({getters, commit}, fontData){
      const {fontFamily} = fontData;
      const edges = getters.getEdges;
      if(!edges) return;
      const layerCurrentID = getters.getCurrentLayerId;

      edges.forEach(edge => {
        if(edge.fontFamily === fontFamily){
          edge.cacheInstanceSetDimensions();
          edge.cache(layerCurrentID);
          getMapAreaService().updateElement(true, edge.id, EDITING_ELEM_TYPES.EDGE);
        }
      })
    },
    updateEdgeHiddenByNode({getters, commit, dispatch}, hiddenData) {
      const nodes = getters.getNodes;
      commit('UPDATE_EDGE_HIDDEN_BY_NODE', {nodes, hiddenData});
      getMapAreaService().updateElement(true, hiddenData.id, EDITING_ELEM_TYPES.EDGE);
    },
    async saveEdgeDesignByTemplate({getters, dispatch}, {edgeData, edgeId, type}) {
      const mapsURL = getters.getMapsURL;
      const edgesURL = getters.getEdgesURL;
      const mapId = getters.getCurrentMap.id;
      const data = {'design': edgeData};
      const message = {success: '', error: ''};
      
      try {
        await ApiService.patch(`${mapsURL}${mapId}${edgesURL}${edgeId}`, data);
        if (!type) return;
        
        const dataSE = {type, data: {edgeId, edgeData}};
        dispatch('simultaneousEditing/syncEdits', dataSE, {root: true});
        getMapAreaService().updateElement(true, edgeId, EDITING_ELEM_TYPES.EDGE);
        
        switch (type) {
          case SE_SYNC_TYPES.EDGE_TEMPLATE_APPLIED: {
            message.success = t('message.templateApplySuccess');
            message.error = t('message.templateApplyError');
            break;
          }
          case SE_SYNC_TYPES.EDGE_TEMPLATE_RESTORED: {
            message.success = t('message.templateRestoreSuccess');
            message.error = t('message.templateRestoreError');
            break;
          }
          case SE_SYNC_TYPES.EDGE_TEMPLATE_CREATED: {
            message.success = t('message.templateCreateSuccess');
            message.error = t('message.templateCreateError');
            break;
          }
        }
        await createToastFromStore(TOASTS_TYPES.SUCCESS, message.success, dispatch);
      } catch (err) {
        await createToastFromStore(TOASTS_TYPES.ERROR, message.error, dispatch);
      }
    },
    nullifyEdge({getters, commit}, edgeId){
      commit('NULLIFY_EDGE_ORIGINAL_VALUES', edgeId);
      const edgesOriginal = getters.getRawEdges;
      const currentEdge = edgesOriginal.find(edge => edge.id === edgeId);
      return currentEdge.design;
    },
    async updateEdgeTemplate({getters, dispatch}, templateData) {
      const {edgeData, edgeId, type, template} = templateData;
      const mapsURL = getters.getMapsURL;
      const edgesURL = getters.getEdgesURL;
      const mapId = getters.getCurrentMap.id;
      const data = {'design': edgeData};
      
      try {
        await ApiService.patch(`${mapsURL}${mapId}${edgesURL}${edgeId}`, data);
        const dataSE = {type, data: {...template, edgeId}};
        dispatch('simultaneousEditing/syncEdits', dataSE, {root: true});
        await createToastFromStore(TOASTS_TYPES.SUCCESS, t('message.templateUpdateSuccess'), dispatch);
      } catch (err) {
        await createToastFromStore(TOASTS_TYPES.ERROR, t('message.templateUpdateError'), dispatch);
      }
    },
    applyTemplateRestoreToEdges({getters, commit}, edgeId) {
      const elementsDesign = getters.getElementsDesign;
      const fonts = getters.getClientFonts;
      commit('APPLY_TEMPLATE_RESTORE_TO_EDGES', {elementsDesign, fonts, edgeId});
      getMapAreaService().updateElement(true, edgeId, EDITING_ELEM_TYPES.EDGE);
    },
    populateCreatedEdgeTemplate({getters, commit, dispatch}, {edgeId, template}) {
      const fonts = getters.getClientFonts;
      const edges = getters.getEdges;
      const currentEdge = edges.find(edge => edge.id === edgeId);
      
      if (!currentEdge) return;
      
      commit('UPDATE_EDGE_TEMPLATE_ID', {templateId: template.id, edgeId: currentEdge.id});
      commit('UPDATE_EDGE_ORIGINAL_TEMPLATE_ID', {templateId: template.id, edgeId: currentEdge.id});
      
      let templateObject = {};
      for (let [key, value] of Object.entries(currentEdge)) {
        if (key === 'fontFamily' && value) value = fontFamilyGetUrlByName(value, fonts);
        Object.assign(templateObject, convertToOriginalEdgeProperty({[key]: value}))
      }
      
      dispatch('design/templateUpdate', {id: template.id, data: templateObject}, {root: true});
      commit('NULLIFY_EDGE_ORIGINAL_VALUES', edgeId);
    },
    applyTemplate({getters, commit, dispatch}, {templateId, edgeId}) {
      const edgeDesign = getEdgeDesignFromTemplate(templateId, getters.getElementsDesign);
      const fonts = getters.getClientFonts;
      
      if (edgeDesign.font_url) edgeDesign.font_url = fontFamilyGetNameByUrl(edgeDesign.font_url, fonts);
      
      commit('UPDATE_EDGE_TEMPLATE_ID', {templateId, edgeId});
      commit('UPDATE_EDGE_ORIGINAL_TEMPLATE_ID', {templateId, edgeId});
      commit('UPDATE_EDITED_EDGE', {edgeData: adaptedOriginalDesignPropertiesToEdge(edgeDesign), edgeId});
      commit('NULLIFY_EDGE_ORIGINAL_VALUES', edgeId);
      getMapAreaService().updateElement(true, edgeId, EDITING_ELEM_TYPES.EDGE);
    },
    applyUpdatedObjectTemplatesToEdges({getters, commit, dispatch}, {templateId}) {
      const elementsDesign = getters.getElementsDesign;
      const fonts = getters.getClientFonts;
      commit('APPLY_OBJECT_TEMPLATES_UPDATED_TO_EDGES', {templateId, elementsDesign, fonts});
    },
    applyDeletedObjectTemplates({getters, commit, dispatch}, templateId) {
      const elementsDesign = getters.getElementsDesign
      const fonts = getters.getClientFonts;
      const templateData = {elementsDesign, templateId, fonts}
      commit('APPLY_OBJECT_TEMPLATES_DELETED', templateData);
      getMapAreaService().renderFrame();
    },
    applyGlobalsUpdateToEdges({getters, commit}) {
      const elementsDesign = getters.getElementsDesign;
      const fonts = getters.getClientFonts;
      const edges = getters.getEdges;
      commit('APPLY_GLOBAL_UPDATE_TO_EDGES', {elementsDesign, fonts});
      
      const edgesToUpdate = edges?.filter(edge => parseInt(edge.template) === 0) || [];
      edgesToUpdate.forEach(edge => {
        getMapAreaService().updateElement(true, edge.id, EDITING_ELEM_TYPES.EDGE);
      });
    },
    setEdgeEditing({commit}, edge) {
      commit('SET_EDGE_EDITING', edge);
      getMapAreaService().updateElement(true, edge.elem_id, EDITING_ELEM_TYPES.EDGE);
    },
    removeEdgeEditing({commit}, edge) {
      commit('REMOVE_EDGE_EDITING', edge);
      getMapAreaService().updateElement(true, edge.elem_id, EDITING_ELEM_TYPES.EDGE);
    }
  }
}